import { FC, useCallback } from 'react';

import * as zod from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  createClientMutationId,
  useSetUserErrorCodes,
  useUserErrorCode,
} from 'shared-lib';
import {
  ActionContainer,
  ErrorField,
  FormErrorCode,
  FormErrorCodes,
  FormInputRow,
  InputField,
} from 'shared-ui';

import {
  IdentityUserChangePasswordInput,
  useIdentityUserChangePassword,
} from '../mutations';
import { IdentityUserAccountsErrorCodes } from '../lib';

const formSchema = zod
  .object({
    originalPassword: zod
      .string()
      .min(1, { message: 'Original Password is required' }),
    password: zod.string().min(8, { message: 'Password is required' }),
    confirmPassword: zod
      .string()
      .min(8, { message: 'Confirm password is required' }),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'], // path of error
  });

type FormSchema = zod.infer<typeof formSchema>;

type Props = {
  identityUserId: string;
  onDone: () => void;
  onCancel: () => void;
};
export const IdentityUserChangePassword: FC<Props> = ({
  identityUserId,
  onDone,
  onCancel,
}) => {
  const setErrorCode = useSetUserErrorCodes();

  const { register, handleSubmit, formState } = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
  });

  const userErrorCode = useUserErrorCode();
  const [changePassword, isChangingPassword] = useIdentityUserChangePassword();

  const onSave = useCallback(
    async (form: FormSchema) => {
      setErrorCode(null);

      if (!form.originalPassword || !form.password) {
        return;
      }

      const input: IdentityUserChangePasswordInput = {
        identityUserId,
        clientMutationId: createClientMutationId(),
        newPassword: form.password!,
        originalPassword: form.originalPassword!,
      };

      const response = await changePassword({ input });

      if (response.data.userErrors && response.data.userErrors.length > 0) {
        setErrorCode(response.data.userErrors);
      }

      if (response.data.payload?.node) {
        onDone();
      }
    },
    [changePassword],
  );

  const onSubmit = handleSubmit(
    async form => {
      await onSave(form);
    },
    invalid => {
      console.error(invalid);
    },
  );

  const { errors, isValid, isDirty } = formState;

  let errorDisplay = <FormErrorCodes />;

  if (
    userErrorCode &&
    userErrorCode === IdentityUserAccountsErrorCodes.USER_EMAIL_IS_IN_USE
  ) {
    errorDisplay = <FormErrorCode message='Email is already in use' />;
  }

  return (
    <div className='m-4 flex flex-col items-center justify-center'>
      {errorDisplay}
      <form onSubmit={onSubmit}>
        <ActionContainer
          isDirty={isDirty}
          showSubmit={true}
          onCancel={onCancel}
          showCancel={true}
          isSaving={isChangingPassword}
          isDisabled={!isValid}
          submitText='Update Password'
          titleTop={`Update Password`}
        >
          <FormInputRow name='originalPassword' title='Original Password *'>
            <InputField
              aria-label='Enter old password'
              register={register}
              name='originalPassword'
              required={true}
            />
            <ErrorField field={errors.originalPassword} />
          </FormInputRow>

          <FormInputRow name='password' title='Password *'>
            <InputField
              aria-label='New password'
              register={register}
              name='password'
              required={true}
            />
            <ErrorField field={errors.password} />
          </FormInputRow>

          <FormInputRow name='confirmPassword' title='Confirm Password *'>
            <InputField
              aria-label='Confirm password'
              register={register}
              name='confirmPassword'
              required={true}
            />
            <ErrorField field={errors.confirmPassword} />
          </FormInputRow>
        </ActionContainer>
      </form>
    </div>
  );
};
